import { useEffect, useRef } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { useAudioRecorder, RecorderState } from '../../hooks/useAudioRecorder';

interface VoiceButtonProps {
  onAudioReady: (blob: Blob) => void;
  disabled?: boolean;
}

export function VoiceButton({ onAudioReady, disabled }: VoiceButtonProps) {
  const { state, startRecording, stopRecording, audioBlob, error, level } =
    useAudioRecorder({ silenceThreshold: -50, silenceDuration: 2, maxDuration: 60 });

  const prevBlobRef = useRef<Blob | null>(null);

  // Fire callback when a new blob is available
  useEffect(() => {
    if (audioBlob && audioBlob !== prevBlobRef.current) {
      prevBlobRef.current = audioBlob;
      onAudioReady(audioBlob);
    }
  }, [audioBlob, onAudioReady]);

  const handleClick = () => {
    if (state === 'idle') {
      startRecording();
    } else if (state === 'recording') {
      stopRecording();
    }
  };

  return (
    <div className="relative flex items-center">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || state === 'processing'}
        title={getTitle(state)}
        className={`
          relative flex items-center justify-center w-9 h-9 rounded-full transition-all duration-200
          ${state === 'recording'
            ? 'bg-red-50 dark:bg-red-900/200 hover:bg-red-600 text-white shadow-lg'
            : state === 'processing'
            ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        {state === 'processing' ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : state === 'recording' ? (
          <Square className="w-3.5 h-3.5 fill-current" />
        ) : (
          <Mic className="w-4 h-4" />
        )}

        {/* Pulse ring when recording */}
        {state === 'recording' && (
          <span
            className="absolute inset-0 rounded-full bg-red-50 dark:bg-red-900/200 opacity-30 animate-ping"
            style={{ animationDuration: `${Math.max(0.5, 1.5 - level / 100)}s` }}
          />
        )}
      </button>

      {/* Level indicator bar */}
      {state === 'recording' && (
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-red-50 dark:bg-red-900/200 transition-all duration-75"
            style={{ width: `${level}%` }}
          />
        </div>
      )}

      {/* Error tooltip */}
      {error && (
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-56 text-xs text-white bg-red-600 rounded px-2 py-1 shadow z-50 text-center">
          {error}
        </div>
      )}
    </div>
  );
}

function getTitle(state: RecorderState): string {
  switch (state) {
    case 'recording': return 'Arrêter l\'enregistrement (ou silence de 2s)';
    case 'processing': return 'Transcription en cours…';
    default: return 'Enregistrement vocal';
  }
}
